import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as remoteConfigService from '../../services/remoteConfigService';

/**
 * @file Renders the footer for the application.
 * This component displays the copyright information and links to the
 * privacy policy and terms of service pages.
 */

/**
 * The footer component.
 *
 * @returns {JSX.Element} The rendered Footer component.
 */
const Footer = () => {
  const [isBetaMode, setIsBetaMode] = useState(false);

  // Check if we're in beta mode
  useEffect(() => {
    const checkBetaMode = async () => {
      try {
        const plansEnabled = await remoteConfigService.getPlansEnabled();
        setIsBetaMode(!plansEnabled);
      } catch (error) {
        console.error('Footer: Error checking beta mode:', error);
        setIsBetaMode(false);
      }
    };
    checkBetaMode();
  }, []);

  return (
    <footer className="w-full bg-gray-900 text-gray-200 py-4 text-center border-t border-gray-800 relative">
      {isBetaMode && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div 
            className="bg-green-800 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg border-2 border-white flex items-center hover:bg-atw-green hover:scale-105 transition-all duration-200"
            title="Welcome to After The Whistle Beta! You have full premium access during our testing phase."
          >
            <span className="hidden sm:inline">BETA VERSION</span>
            <span className="sm:hidden">BETA</span>
          </div>
        </div>
      )}
      <div className="container mx-auto flex flex-col md:flex-row justify-center items-center gap-4 mt-2">
        <span className="text-sm">&copy; {new Date().getFullYear()} After the Whistle LLC</span>
        <span className="mx-2">|</span>
        <Link to="/privacy-policy" className="hover:underline text-sm">Privacy Policy</Link>
        <span className="mx-2">|</span>
        <Link to="/terms-of-service" className="hover:underline text-sm">Terms of Service</Link>
        {isBetaMode && (
          <>
            <span className="mx-2">|</span>
            <span className="text-sm">
              Beta version - Premium features unlocked!
            </span>
          </>
        )}
        <span className="mx-2">|</span>
        <span className="text-sm">Made with ❤️ by Fans for Fans</span>
      </div>
    </footer>
  );
};

export default Footer;
